import React from 'react';
import { ShipmentData } from '../../lib/supabase';
import { Edit, ExternalLink, Package, Truck, Ship, FileText } from 'lucide-react';
import { formatDate } from '../../utils/dates';

interface ShipmentTableProps {
  shipments: ShipmentData[];
  onView: (shipment: ShipmentData) => void;
  onEdit: (shipment: ShipmentData) => void;
}

const ShipmentTable: React.FC<ShipmentTableProps> = ({ shipments, onView, onEdit }) => {
  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'draft':
        return <FileText className="h-5 w-5 text-gray-500" />;
      case 'booked':
        return <Package className="h-5 w-5 text-blue-500" />;
      case 'in_transit':
        return <Ship className="h-5 w-5 text-yellow-500" />;
      case 'customs':
        return <FileText className="h-5 w-5 text-orange-500" />;
      case 'delivered':
        return <Truck className="h-5 w-5 text-green-500" />;
      default:
        return <Package className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <div className="min-w-full divide-y divide-gray-200">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              BL Number
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Customer
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Route
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Vessel
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              ETD
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              ETA
            </th>
            <th scope="col" className="relative px-6 py-3">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {shipments.map((shipment) => (
            <tr key={shipment.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {shipment.bl_number}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {shipment.customer}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {shipment.origin} → {shipment.destination}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {shipment.vessel} ({shipment.voyage})
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {shipment.departure_date ? formatDate(new Date(shipment.departure_date)) : '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {shipment.estimated_arrival ? formatDate(new Date(shipment.estimated_arrival)) : '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <button
                  onClick={() => onView(shipment)}
                  className="text-primary-600 hover:text-primary-900 mr-4"
                >
                  <ExternalLink className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onEdit(shipment)}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <Edit className="h-4 w-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ShipmentTable;